import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ClosingCrossSVG from "../../custom-components/svg/functional-icons/ClosingCrossSVG";

type ImageUploaderProps = {
    onChange: (file: File | null) => void;
    deleteImage: () => void;
    imageUrl: string;
};

export function ImageUploader({ onChange, imageUrl, deleteImage }: ImageUploaderProps) {
    const [previewImage, setPreviewImage] = useState<string | null>(null);
    const [imageName, setImageName] = useState<string | null>(null);
    const { t } = useTranslation();
    const inputRef = useRef<HTMLInputElement | null>(null);

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setImageName(file.name);
        onChange(file);

        const reader = new FileReader();
        reader.onloadend = () => {
            setPreviewImage(reader.result as string);
        };
        reader.readAsDataURL(file);
    };

    const handleRemoveImage = () => {
        setPreviewImage(null);
        setImageName(null);
        deleteImage();
        if (inputRef.current) inputRef.current.value = "";
    };

    const renderImageBlock = (src: string, showName = false) => (
        <div className="w-full">
            <div className="flex justify-between items-center mb-2">
                <img
                    src={src}
                    width={96}
                    height={96}
                    alt="Image Preview"
                    className="rounded-xl max-w-[200px] object-cover border"
                />
                {showName && <span>{imageName}</span>}
                <button className="p-2 hover:bg-red-200 rounded-md" type="button" onClick={handleRemoveImage}>
                    <ClosingCrossSVG fill="red" />
                </button>
            </div>
        </div>
    );

    return (
        <div className="flex flex-col gap-2 items-start">
            {previewImage ? (
                renderImageBlock(previewImage, true)
            ) : imageUrl ? (
                renderImageBlock(imageUrl)
            ) : (
                <label
                    htmlFor="image-upload"
                    className="flex items-center justify-center w-full h-24 border-2 border-dashed rounded-xl cursor-pointer hover:bg-accent hover:border-muted-foreground"
                >
                    <span className="text-sm text-light-text-200 dark:text-dark-text-400">
                        {t("console.common.uploadImage")}
                    </span>
                </label>
            )}
            <input
                id="image-upload"
                type="file"
                ref={inputRef}
                accept="image/jpeg,image/png,image/webp,image/svg+xml"
                onChange={handleImageChange}
                className="sr-only"
            />
        </div>
    );
}
